import type { ItemLegacyDTO } from '@tempo-io/roadmap-dto';
import type { CustomFieldTypes } from '@/common/api/types/customField';
import {
  DependencyType,
  type Item,
  type ItemCustomFieldValue,
  type ItemCustomFieldValues,
  SchedulingType,
} from '@/common/api/types/items';

// Unlike customFieldV2ToV1 (which unwraps the { value, description } envelope from the live API),
// this handles raw primitives stored directly in published artifacts (e.g. "foo", 5).
function wrapRawCustomFieldValue(
  rawValue: unknown,
  fieldType: CustomFieldTypes,
): ItemCustomFieldValue | undefined {
  if (rawValue === null || rawValue === undefined) return undefined;

  if (
    typeof rawValue === 'object' &&
    !Array.isArray(rawValue) &&
    fieldType in (rawValue as object)
  ) {
    return rawValue as ItemCustomFieldValue;
  }

  switch (fieldType) {
    case 'CHECKBOX':
      return { CHECKBOX: rawValue as boolean };
    case 'TEXT':
      return { TEXT: rawValue as string };
    case 'MULTILINE_TEXT':
      return { MULTILINE_TEXT: rawValue as string };
    case 'NOTE':
      return { NOTE: rawValue as string };
    case 'NUMERIC':
      return { NUMERIC: rawValue as number };
    case 'CURRENCY':
      return { CURRENCY: rawValue as number };
    case 'PICKLIST':
      return { PICKLIST: rawValue as number };
    case 'ITEM':
      return { ITEM: rawValue as number };
    case 'USER':
      return { USER: rawValue as string };
    case 'DATE': {
      if (typeof rawValue === 'string') return { DATE: rawValue };
      if (typeof rawValue === 'object') {
        const d = rawValue as { year?: number; month?: number; day?: number };
        if (d.year === undefined || d.month === undefined || d.day === undefined) return undefined;
        const month = String(d.month).padStart(2, '0');
        const day = String(d.day).padStart(2, '0');
        return { DATE: `${d.year}-${month}-${day}` };
      }
      return undefined;
    }
    case 'LINK': {
      if (typeof rawValue === 'object' && 'value' in (rawValue as object)) {
        const linkObj = rawValue as { value: string; description?: string };
        return { LINK: linkObj.value, description: linkObj.description };
      }
      return { LINK: rawValue as string };
    }
    case 'MULTI_PICKLIST':
    case 'MULTI_ITEM':
    case 'MULTI_USER':
    case 'MULTI_CUSTOMER':
    case 'MULTI_TEXT':
    case 'MULTI_LINK': {
      const entries = Array.isArray(rawValue) ? rawValue : [rawValue];
      const objectEntries = entries.filter(
        (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null,
      );
      if (objectEntries.length > 0) {
        return objectEntries.map((entry) => {
          if (fieldType in entry) return entry;
          return { id: entry.id, [fieldType]: entry.value };
        }) as ItemCustomFieldValue;
      }
      const primitiveEntries = entries.filter(
        (entry): entry is string | number => typeof entry === 'string' || typeof entry === 'number',
      );
      if (primitiveEntries.length > 0) {
        return primitiveEntries.map((v) => ({ id: v, [fieldType]: v })) as ItemCustomFieldValue;
      }
      return undefined;
    }
    default: {
      const _exhaustiveCheck: never = fieldType;
      return _exhaustiveCheck;
    }
  }
}

export function mapCustomFieldValues(
  dtoValues: ItemLegacyDTO['customFields'] | Record<string, unknown>,
  fieldDefs: ReadonlyArray<{ id: number; type: CustomFieldTypes }>,
): ItemCustomFieldValues {
  if (!dtoValues) return {};
  const typeById = new Map(fieldDefs.map((f) => [String(f.id), f.type]));
  const result: ItemCustomFieldValues = {};
  for (const [key, rawValue] of Object.entries(dtoValues)) {
    if (rawValue === null || rawValue === undefined) continue;
    const fieldType = typeById.get(key);
    if (!fieldType) continue;
    const wrapped = wrapRawCustomFieldValue(rawValue, fieldType);
    if (wrapped !== undefined) {
      result[key] = wrapped;
    }
  }
  return result;
}

export function mapLoopItemToItem(
  dto: ItemLegacyDTO,
  fieldDefs: ReadonlyArray<{ id: number; type: CustomFieldTypes }> = [],
  loopId = 0,
): Item {
  return {
    id: dto.id,
    name: dto.name ?? '',
    descriptionSyncSkipped: false,
    description: dto.description ?? null,
    priority: dto.priority,
    itemTypeId: dto.itemTypeId,
    itemStatusId: dto.itemStatusId,
    parentId: dto.parentId ?? null,
    schedulingType: dto.schedulingType ?? SchedulingType.PLANNED_AND_ACTUALS,
    teamId: dto.teamId ?? null,
    estimationId: dto.estimationId ?? null,
    timeSpentSeconds: dto.timeSpentSeconds ?? null,
    earliestStartDateTime: dto.earliestStartDateTime ?? null,
    deadlineDateTime: dto.deadlineDateTime ?? null,
    plannedStartDateTime: dto.plannedStartDateTime ?? null,
    plannedFinishDateTime: dto.plannedFinishDateTime ?? null,
    doneDateTime: dto.doneDateTime ?? null,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
    externalKey: dto.externalKey,
    externalLink: dto.externalLink,
    planId: dto.planId,
    loopId,
    customFields: mapCustomFieldValues(dto.customFields, fieldDefs),
    dependencies: dto.dependencies.map((dep) => ({
      dependencyType: dep.type ?? DependencyType.Predecessor,
      dependencyItemId: dep.itemId,
    })),
  };
}
